/**
 * SimulateCard — «Проверить бота»: сообщение «от пациента» уходит в боевой
 * конвейер ассистента, ответ и хвост диалога видны сразу. Переписка как в
 * WhatsApp: новые снизу, своё сообщение видно сразу, лента сама едет к
 * последнему. Нужны привязанный номер и включённый режим. В режиме «Сам»
 * ответ уйдёт на указанный номер по-настоящему — поэтому по умолчанию пусто.
 */

import {type ChangeEvent, type KeyboardEvent, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {SendHorizontal} from 'lucide-react';

import {type SimulatedMessage, simulateInbound} from '@services/assistant';
import {Field, TextInput} from '@components/FormKit';
import {Button} from '@components/ui/Button';

import {SectionCard} from '../../SectionCard';

import {SimulateFeed} from './SimulateFeed';

const OUTCOMES: Record<string, string> = {
  sent: 'Ответ ушёл пациенту',
  shadow: 'Наблюдает: ответ готов, пациенту не ушёл',
  draft: 'Черновик ждёт человека',
  escalated: 'Передано человеку',
  skipped: 'Бот промолчал (режим выключен или нет привязки)',
  deferred: 'Отложено',
};

type Props = {clientId: string};

export const SimulateCard = ({clientId}: Props) => {
  const [phone, setPhone] = useState('');
  const [text, setText] = useState('');
  /** Хвост диалога с сервера, новые снизу */
  const [thread, setThread] = useState<SimulatedMessage[]>([]);
  const mutation = useMutation({
    mutationFn: simulateInbound,
    onSuccess: (r) => setThread(r.messages),
    // Не прошло — текст обратно в поле, чтобы отправить ещё раз
    onError: (_, vars) => setText(vars.text),
  });
  const res = mutation.data;
  /** Своё сообщение в пути — в ленте сразу, пока бот думает */
  const outgoing = mutation.isPending ? mutation.variables.text.trim() : null;
  /** Итог последней отправки — под лентой, как служебная строка WhatsApp */
  const note =
    res && outgoing === null
      ? [OUTCOMES[res.outcome] ?? res.outcome, res.escalationReason].filter(Boolean).join(' · ')
      : null;
  const canSend = phone.replace(/\D/g, '').length >= 9 && text.trim() !== '' && !mutation.isPending;

  const handleSend = () => {
    if (!canSend) return;
    mutation.mutate({clinicId: clientId, fromPhone: phone, text});
    setText('');
  };
  /** Enter — отправить, Shift+Enter — новая строка (как в WhatsApp Web) */
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    handleSend();
  };
  /** Другой номер — другой диалог: прежняя переписка не его */
  const handlePhone = (e: ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
    setThread([]);
    mutation.reset();
  };
  const handleText = (e: ChangeEvent<HTMLTextAreaElement>) =>
    setText(e.target.value.slice(0, 1000));

  return (
    <SectionCard
      title="Проверить бота"
      hint="В режиме «Сам» ответ по-настоящему уйдёт на этот номер в WhatsApp — используйте свой."
    >
      <div className="flex flex-col gap-3">
        <Field label="Номер «пациента»">
          <TextInput
            value={phone}
            onChange={handlePhone}
            placeholder="+996 …"
            inputMode="tel"
            mono
          />
        </Field>

        <SimulateFeed thread={thread} outgoing={outgoing} note={note} />

        {mutation.isError && (
          <div className="rounded-[10px] bg-destructive/10 px-3.5 py-2.5 text-[12.5px] font-medium text-destructive">
            Не прошло. Проверьте, что номер WhatsApp привязан и режим включён
          </div>
        )}

        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={handleText}
            onKeyDown={handleKeyDown}
            placeholder="Сколько стоит чистка? Можно завтра после обеда?"
            aria-label="Сообщение"
            rows={2}
            className="min-w-0 flex-1 resize-none rounded-[10px] border border-input bg-card p-[11px_13px] text-sm leading-[1.5] outline-none transition focus:border-primary focus:ring-[3px] focus:ring-primary/12"
          />
          <Button
            className="size-11 shrink-0 p-0"
            disabled={!canSend}
            onClick={handleSend}
            aria-label="Отправить боту"
            title="Отправить боту (Enter)"
          >
            <SendHorizontal className="size-4" />
          </Button>
        </div>
      </div>
    </SectionCard>
  );
};
